import React, { useMemo, useState } from "react";

// The useful thing to do with seventy-two entries is cut them down, and that is
// an interaction rather than a page. Three ways in — the rank, the size of the
// following, and the name — because those are the only three things the text
// itself gives for every one of them.
//
// The order the ranks are declared in is the order the Goetia's own appendix
// lists them: Kings first, then Princes, and so down.
const RANKS = ["King", "Prince", "Duke", "Marquis", "Earl", "President", "Knight"];

// The book's plurals. `${rank}s` made "Marquiss".
const PLURAL = { Marquis: "Marquises" };

const ORDERS = [
  ["number", "In the book's order"],
  ["legions", "Most legions first"],
  ["name", "By name"]
];

function Sigil({ spirit }) {
  if (!spirit.sigil) {
    return <span className="spirit__sigil spirit__sigil--none" aria-hidden="true" />;
  }
  return (
    <img
      className="spirit__sigil"
      src={spirit.sigil}
      alt=""
      loading="lazy"
      decoding="async"
      width="72"
      height="72"
    />
  );
}

export default function SpiritList({ spirits }) {
  const [rank, setRank] = useState(null);
  const [order, setOrder] = useState("number");
  const [query, setQuery] = useState("");
  const [searchEntries, setSearchEntries] = useState(false);

  const counts = useMemo(() => {
    const byRank = {};
    spirits.forEach((s) => {
      (s.ranks || [s.rank]).forEach((r) => {
        byRank[r] = (byRank[r] || 0) + 1;
      });
    });
    return byRank;
  }, [spirits]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = spirits.filter((spirit) => {
      // Match ANY title the appendix gives. Seven spirits hold two, and a
      // reader filtering for Earls should find Vine, who is also a King.
      if (rank && !(spirit.ranks || [spirit.rank]).includes(rank)) return false;
      if (!needle) return true;
      // Alternate spellings count: a reader who has met Flauros should find
      // Haures, because the book says they are the same spirit.
      if (searchEntries && spirit.entry.toLowerCase().includes(needle)) return true;
      return [spirit.name, ...(spirit.alternateNames || [])]
        .some((name) => name.toLowerCase().includes(needle));
    });

    const sorted = [...matches];
    if (order === "legions") sorted.sort((a, b) => b.legions - a.legions || a.number - b.number);
    if (order === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
    return sorted;
  }, [spirits, rank, order, query, searchEntries]);

  const legions = shown.reduce((total, s) => total + s.legions, 0);

  return (
    <div className="spirits" data-testid="spirit-list">
      <div className="spirits__controls">
        <fieldset className="spirits__ranks">
          <legend className="spirits__legend">Rank</legend>
          <button
            type="button"
            className={rank === null ? "chip chip--on" : "chip"}
            aria-pressed={rank === null}
            onClick={() => setRank(null)}
            data-testid="rank-all"
          >
            All <span className="chip__count">{spirits.length}</span>
          </button>
          {RANKS.filter((r) => counts[r]).map((r) => (
            <button
              key={r}
              type="button"
              className={rank === r ? "chip chip--on" : "chip"}
              aria-pressed={rank === r}
              onClick={() => setRank(rank === r ? null : r)}
              data-testid={`rank-${r.toLowerCase()}`}
            >
              {PLURAL[r] || `${r}s`} <span className="chip__count">{counts[r]}</span>
            </button>
          ))}
        </fieldset>

        <div className="spirits__tools">
          <label className="spirits__search">
            <span className="visually-hidden">Find a spirit by name or entry text</span>
            <input
              type="search"
              value={query}
              placeholder={searchEntries ? "Search the 1904 entries…" : "Find a name — Bael, Flauros…"}
              onChange={(event) => setQuery(event.target.value)}
              data-testid="spirit-search"
            />
          </label>

          <label className="spirits__entry-search">
            <input
              type="checkbox"
              checked={searchEntries}
              onChange={(event) => setSearchEntries(event.target.checked)}
              data-testid="spirit-search-entries"
            />
            Include the book's descriptions
          </label>

          <label className="spirits__order">
            <span className="visually-hidden">Order</span>
            <select
              value={order}
              onChange={(event) => setOrder(event.target.value)}
              data-testid="spirit-order"
            >
              {ORDERS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <p className="spirits__count" aria-live="polite" data-testid="spirit-count">
        {shown.length === spirits.length
          ? `All seventy-two, commanding ${legions.toLocaleString()} legions between them.`
          : `${shown.length} of 72, commanding ${legions.toLocaleString()} legions.`}
        {rank && counts[rank] !== undefined && (
          <span className="spirits__aside">
            {" "}The counts overlap: seven spirits hold two titles, so the ranks
            sum to more than seventy-two.
          </span>
        )}
      </p>

      {shown.length === 0 ? (
        <p className="spirits__empty" data-testid="spirit-empty">
          {searchEntries
            ? "No entry contains that phrase. Try another spelling or search the names alone."
            : "No spirit of that name is in the book. The Goetia has seventy-two and no more; a name that is not among them belongs to some other text."}
        </p>
      ) : (
        <ol className="spirit-grid">
          {shown.map((spirit) => (
            <li className="spirit" key={spirit.number}>
              <a className="spirit__link" href={spirit.path} data-testid={`spirit-${spirit.slug}`}>
                <Sigil spirit={spirit} />
                <span className="spirit__body">
                  <span className="spirit__head">
                    <span className="spirit__number">{spirit.number}</span>
                    <span className="spirit__name">{spirit.name}</span>
                  </span>
                  <span className="spirit__meta">
                    {(spirit.ranks || [spirit.rank]).join(" and ")} · {spirit.legions} legions
                    {spirit.sealMetal && ` · seal in ${spirit.sealMetal.toLowerCase()}`}
                  </span>
                  {spirit.alternateNames?.length > 0 && (
                    <span className="spirit__also">
                      also {spirit.alternateNames.join(", ")}
                    </span>
                  )}
                  <span className="spirit__opening">{spirit.opening}</span>
                </span>
              </a>
              <details className="spirit__disclosure" data-testid={`entry-${spirit.slug}`}>
                <summary>Read the 1904 entry</summary>
                <blockquote>
                  <p>{spirit.entry}</p>
                  <footer><cite>The Goetia</cite>, Mathers translation, Crowley edition (1904), spirit {spirit.number}. <a href={spirit.path}>Entry and source notes →</a></footer>
                </blockquote>
              </details>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
